import React from "react";
import { Box, Typography, Chip, Alert, Grid } from "@mui/material";
import { formatCurrency } from "../utils/utils";

interface GSTViewProps {
  amount: number | string;
  gstPercentage: number | string;
  inclusiveGst?: boolean;
}

const GSTView: React.FC<GSTViewProps> = ({
  amount,
  gstPercentage,
  inclusiveGst = false,
}) => {
  const baseAmount = Number(amount) || 0;
  const gstPercent = Number(gstPercentage) || 0;

  let gstAmount: number;
  let totalAmountWithGST: number;

  if (inclusiveGst) {
    // If GST is inclusive, the contract amount already includes GST
    // Calculate GST amount from the inclusive amount
    gstAmount = (baseAmount * gstPercent) / (100 + gstPercent);
    totalAmountWithGST = baseAmount; // Total remains the same
  } else {
    // If GST is exclusive, calculate GST amount and add to base amount
    gstAmount = (baseAmount * gstPercent) / 100;
    totalAmountWithGST = baseAmount + gstAmount;
  }
  const cgst = gstAmount / 2;
  const sgst = gstAmount / 2;

  if (!baseAmount || !gstPercent || gstPercent <= 0) {
    return null;
  }

  if (gstPercent > 100) {
    return (
      <Alert severity="error" sx={{ fontSize: "0.725rem", p: 0.5 }}>
        GST percentage cannot be greater than 100%
      </Alert>
    );
  }

  return (
    <Box>
      <Typography
        variant="subtitle2"
        color="text.primary"
        sx={{ fontSize: "0.75rem", mb: 0.75 }}
      >
        GST Breakdown
      </Typography>
      {inclusiveGst && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 0.5,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.725rem" }}
          >
            Base Amount:
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 300, fontSize: "0.725rem" }}
          >
            {formatCurrency(baseAmount - gstAmount)}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: "0.725rem" }}
        >
          Total GST:
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 300, fontSize: "0.725rem" }}
        >
          {formatCurrency(gstAmount)}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: "0.725rem" }}
        >
          CGST:
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 300, fontSize: "0.725rem" }}
        >
          {formatCurrency(cgst)}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: "0.725rem" }}
        >
          SGST:
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 300, fontSize: "0.725rem" }}
        >
          {formatCurrency(sgst)}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 0.5,
          py: 0.5,
        }}
        borderTop="1px dashed #EEEEEE"
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: "0.8rem", fontWeight: "bold" }}
        >
          {inclusiveGst ? "Total (Excl. GST):" : "Total (Incl. GST):"}
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, fontSize: "0.8rem" }}
        >
          {formatCurrency(totalAmountWithGST)}
        </Typography>
      </Box>
    </Box>
  );
};

export default GSTView;
